import { useEffect, useRef, useState } from "react";
import api, { fmt } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Minus, Trash2, Loader2, Banknote } from "lucide-react";
import { useAuth, canDo } from "@/context/AuthContext";

const Stat = ({ label, value, tone = "text-slate-900", testid }) => (
    <div className="hdb-card px-4 py-3">
        <div className="text-[10px] uppercase tracking-wider text-slate-500 font-bold">{label}</div>
        <div className={`hdb-num text-lg font-black ${tone}`} data-testid={testid}>{fmt(value)}</div>
    </div>
);

export const PettyCashTab = () => {
    const { user } = useAuth();
    const isAdmin = canDo(user, "admin");
    const [month, setMonth] = useState(() => new Date().toISOString().slice(0, 7));
    const [data, setData] = useState(null);
    const [form, setForm] = useState(null);

    const reqId = useRef(0);
    const load = () => {
        const id = ++reqId.current;
        return api.get("/petty-cash", { params: { month } })
        .then((r) => { if (id === reqId.current) setData(r.data); })
        .catch((e) => toast.error(e.response?.data?.detail || "Couldn't load petty cash"));
    };
    useEffect(() => { load(); /* eslint-disable-next-line */ }, [month]);

    const del = async (row) => {
        if (!window.confirm(`Delete this ${row.type === "topup" ? "top-up" : "cash-out"} of ${fmt(row.credit || row.debit)}?`)) return;
        try { await api.delete(`/petty-cash/${row.id}`); toast.success("Entry deleted"); load(); }
        catch (e) { toast.error(e.response?.data?.detail || "Delete failed"); }
    };

    return (
        <div data-testid="petty-cash-tab">
            <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
                <input type="month" value={month} onChange={(e) => setMonth(e.target.value)}
                       className="hdb-input hdb-mono !w-44" data-testid="petty-month"/>
                {isAdmin && (
                    <div className="flex gap-2">
                        <button className="hdb-btn hdb-btn-mint" data-testid="petty-topup-btn"
                                onClick={() => setForm({ type: "topup" })}>
                            <Plus size={13}/> Add cash
                        </button>
                        <button className="hdb-btn hdb-btn-ghost" data-testid="petty-cashout-btn"
                                onClick={() => setForm({ type: "cashout" })}>
                            <Minus size={13}/> Cash out
                        </button>
                    </div>
                )}
            </div>
            {!data ? (
                <div className="p-10 text-center text-slate-400"><Loader2 className="inline animate-spin" size={14}/></div>
            ) : (
                <>
                    <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-4">
                        <Stat label="Opening" value={data.opening} testid="petty-opening"/>
                        <Stat label="Cash added" value={data.topups} tone="text-emerald-700" testid="petty-topups"/>
                        <Stat label="Cash spent" value={data.spent} tone="text-rose-700" testid="petty-spent"/>
                        <Stat label="Closing" value={data.closing} testid="petty-closing"/>
                        <Stat label="Cash in hand now" value={data.current_balance}
                              tone={data.current_balance < 0 ? "text-rose-700" : "text-slate-900"}
                              testid="petty-current"/>
                    </div>
                    <PettyTable data={data} isAdmin={isAdmin} onDelete={del}/>
                    <p className="text-[11px] text-slate-400 mt-2">
                        Approved expenses paid in cash are deducted automatically. Pending ones are not counted until approved.
                    </p>
                </>
            )}
            {form && <PettyForm type={form.type} onClose={() => setForm(null)}
                                onSaved={() => { setForm(null); load(); }}/>}
        </div>
    );
};

const PettyTable = ({ data, isAdmin, onDelete }) => (
    <div className="hdb-card overflow-hidden">
        {data.rows.length === 0 ? (
            <div className="p-10 text-center text-slate-400" data-testid="petty-empty">
                <Banknote className="inline mr-2" size={14}/> No petty cash movement in {data.month}.
            </div>
        ) : (
            <table className="hdb-table w-full text-sm">
                <thead><tr>
                    <th className="text-left">Date</th><th className="text-left">Type</th>
                    <th className="text-left">Narration</th><th className="text-right">In</th>
                    <th className="text-right">Out</th><th className="text-right">Balance</th>
                    {isAdmin && <th/>}
                </tr></thead>
                <tbody>
                    {data.rows.map((r, i) => (
                        <tr key={`${r.source}-${r.id}`} data-testid={`petty-row-${i}`}>
                            <td className="hdb-mono whitespace-nowrap">{r.date}</td>
                            <td className="text-xs font-bold">
                                {r.type === "topup" ? <span className="text-emerald-700">Cash added</span>
                                    : r.type === "cashout" ? <span className="text-rose-700">Cash out</span>
                                    : <span className="text-slate-600">Expense</span>}
                            </td>
                            <td className="text-slate-700">{r.narration || "—"}</td>
                            <td className="hdb-num hdb-num-right text-emerald-700">{r.credit ? fmt(r.credit) : ""}</td>
                            <td className="hdb-num hdb-num-right text-rose-700">{r.debit ? fmt(r.debit) : ""}</td>
                            <td className="hdb-num hdb-num-right font-bold" data-testid={`petty-balance-${i}`}>{fmt(r.balance)}</td>
                            {isAdmin && (
                                <td className="text-right">
                                    {r.source === "petty" && (
                                        <button onClick={() => onDelete(r)} className="hdb-btn hdb-btn-ghost text-xs text-red-700"
                                                data-testid={`petty-delete-${i}`}><Trash2 size={11}/></button>
                                    )}
                                </td>
                            )}
                        </tr>
                    ))}
                </tbody>
            </table>
        )}
    </div>
);

const PettyForm = ({ type, onClose, onSaved }) => {
    const [f, setF] = useState({ entry_date: new Date().toISOString().slice(0, 10), amount: "", narration: "" });
    const [saving, setSaving] = useState(false);
    const save = async () => {
        if (!(parseFloat(f.amount) > 0)) return toast.error("Amount must be greater than zero");
        setSaving(true);
        try {
            await api.post("/petty-cash", { ...f, type, amount: parseFloat(f.amount) });
            toast.success(type === "topup" ? "Cash added" : "Cash-out recorded");
            onSaved();
        } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
        finally { setSaving(false); }
    };
    return (
        <div className="fixed inset-0 z-40 bg-slate-900/60 backdrop-blur-sm flex items-center justify-center p-4"
             onClick={onClose} data-testid="petty-modal">
            <div className="w-full max-w-md bg-white rounded-xl shadow-2xl p-5" onClick={(e) => e.stopPropagation()}>
                <div className="font-black text-lg mb-4">{type === "topup" ? "Add cash to petty box" : "Record cash out"}</div>
                <div className="grid grid-cols-2 gap-3">
                    <div>
                        <label className="hdb-label">Date</label>
                        <input type="date" className="hdb-input hdb-mono" value={f.entry_date}
                               onChange={(e) => setF({ ...f, entry_date: e.target.value })} data-testid="petty-form-date"/>
                    </div>
                    <div>
                        <label className="hdb-label">Amount (₹)</label>
                        <input type="number" min="0" step="0.01" placeholder="0.00" className="hdb-input hdb-mono text-right"
                               value={f.amount} onChange={(e) => setF({ ...f, amount: e.target.value })} data-testid="petty-form-amount"/>
                    </div>
                </div>
                <label className="hdb-label mt-3 block">Narration</label>
                <input className="hdb-input w-full" placeholder={type === "topup" ? "e.g. Withdrawn from bank" : "e.g. Returned to bank"}
                       value={f.narration} onChange={(e) => setF({ ...f, narration: e.target.value })} data-testid="petty-form-narration"/>
                <div className="mt-5 flex justify-end gap-2">
                    <button onClick={onClose} className="hdb-btn hdb-btn-ghost" data-testid="petty-form-cancel">Cancel</button>
                    <button onClick={save} disabled={saving} className="hdb-btn hdb-btn-primary" data-testid="petty-form-save">
                        {saving && <Loader2 className="animate-spin" size={13}/>} Save
                    </button>
                </div>
            </div>
        </div>
    );
};
